import { Map, List } from 'immutable';
import Enum from 'enum';
import moment from 'moment';
import countries from 'i18n-iso-countries';

import { ContactType } from 'flow/utils/extension-utils';
import {
    getCountryCodeFromNumber,
    prettyFormatE164Number,
} from 'flow/utils/call-utils';

export const ADDRESS_KEY = 'address';

export const ContactFieldOrder = List([
    'anumber',
    'mobile',
    'email',
    'companyname',
    'department',
    'occupation',
    'address',
    'country',
    'description',
    'keywords',
]);

export const NameFields = List(['firstname', 'lastname']);

export const AddressFields = List(['address', 'postalcode', 'city']);

const isTechnicalNumberContact = (contact, phoneNumber) =>
    contact?.getIn(['technicalNumber', 'number'], '') === phoneNumber;

export const hasAddressOptions = (contact) => {
    return (
        contact.get('address') ||
        contact.get('postalcode') ||
        contact.get('city')
    );
};

export const ContactFieldType = new Enum(['BASIC_STRING', 'EMAIL', 'NUMBER'], {
    freez: true,
    ignoreCase: true,
});

/**
 * Contact field length restrictions
 * gathered from base/UserContact.java in Core.
 * IgnoreCase for also matching contactDTO
 * format usage in create-contact.jsx.
 */
export const ContactFieldMaxLength = new Enum(
    {
        firstname: 60,
        lastname: 60,
        description: 40,
        anumber: 20,
        mobile: 20,
        adress: 50,
        address: 50,
        postalcode: 10,
        city: 20,
        country: 30,
        email: 255,
        companyname: 40,
        occupation: 40,
        department: 40,
        regnr: 13,
        alttelephone1: 25,
        alttelephone2: 25,
        keywords: 1024,
    },
    { ignoreCase: true },
);

export const ContactTypeToIndex = Map({
    bound: 'flowContacts',
    personal: 'externalContacts',
    shared: 'externalContacts',
    global: 'externalContacts',
    hidden: 'flowContacts',
});

export const shouldRenderContactField = (_fieldData, fieldKey) => {
    const isNameField =
        fieldKey.toLowerCase() === 'firstname' ||
        fieldKey.toLowerCase() === 'lastname';
    const isOrderedField = ContactFieldOrder.includes(fieldKey.toLowerCase());
    const isAddressField = AddressFields.includes(fieldKey.toLowerCase());
    const isUnderscore = fieldKey.startsWith('_');

    return !(isNameField || isOrderedField || isAddressField || isUnderscore);
};

export const getContactName = (contact) => {
    if (!contact) {
        return undefined;
    }

    const firstName = contact.get('firstName');
    const lastName = contact.get('lastName');

    if (firstName && !lastName) {
        return firstName;
    }

    if (!firstName && lastName) {
        return lastName;
    }

    if (firstName && lastName) {
        return `${firstName} ${lastName}`;
    }

    return undefined;
};

export const getContactDepartment = (contact) => {
    if (!contact) {
        return undefined;
    }

    const department = contact.get('department');
    const isBound = contact.get('type') === ContactType.bound.key;

    if (department && isBound) {
        return department;
    }

    return undefined;
};

export const getContactCompanyName = (contact) => {
    if (!contact) {
        return undefined;
    }

    return contact.get('companyName');
};

export const getContactNameOrCompanyName = (contact) => {
    return getContactName(contact) || getContactCompanyName(contact);
};

export const getContactFromSourcesByEmail = (contacts, email) => {
    if (!email) {
        return null;
    }
    return contacts?.find(
        (contact) => contact && contact.get('email') === email,
    );
};

export const getContactNumber = (contact) => {
    if (!contact) {
        return undefined;
    }

    return (
        contact.get('fixedNumber') ||
        contact.get('mobileNumber') ||
        contact.getIn(['fixed', 'number'])
    );
};

export const getBankidName = (agentInfo) => {
    if (!agentInfo || agentInfo.get('status') !== 'SUCCESS') {
        return undefined;
    }
    // Convert uppercase to uppercase initial letters, words separated by whitespace or dash(-)
    const splitString = agentInfo.get('name').split(/(\s|-)/);
    return splitString
        .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
        .join('');
};

export const getCallItemContactName = (contact, agentInfo) => {
    const bankidName = getBankidName(agentInfo);
    if (bankidName) {
        return bankidName;
    }
    const name = getContactName(contact);
    if (name) {
        return name;
    }
    return undefined;
};

export const getNumberFromThirdPartyContact = (thirdPartyContact) => {
    if (!thirdPartyContact) {
        return null;
    }
    return thirdPartyContact.getIn(['phoneNumber', 'number']);
};

const ContactRowType = Object.freeze({
    CONTACT_NAME: 'CONTACT_NAME',
    EMAIL: 'EMAIL',
    NUMBER: 'NUMBER',
    DEPARTMENT_NAME: 'DEPARTMENT_NAME',
    COMPANY_NAME: 'COMPANY_NAME',
    COUNTRY_NAME: 'COUNTRY_NAME',
    UNKNOWN: 'UNKNOWN',
    HIDDEN: 'HIDDEN',
    TECHNICAL_NUMBER: 'TECHNICAL_NUMBER',
});

export const getCallItemContactTitle = (
    contact,
    agentInfo,
    phoneNumber,
    ownCountryCode,
    i18next,
) => {
    const contactName = getCallItemContactName(contact, agentInfo);
    if (contactName) {
        return {
            text: contactName,
            type: ContactRowType.CONTACT_NAME,
        };
    }
    const companyName = getContactCompanyName(contact);
    if (companyName) {
        return {
            text: companyName,
            type: ContactRowType.COMPANY_NAME,
        };
    }
    if (
        phoneNumber &&
        phoneNumber !== 'unknown' &&
        phoneNumber !== 'anonymous'
    ) {
        // the 'unknown' string comes from the NumberDTO
        return {
            text: prettyFormatE164Number(phoneNumber, ownCountryCode),
            type: ContactRowType.NUMBER,
        };
    }
    return {
        text: i18next.t('Contact.Unknown'),
        type: ContactRowType.UNKNOWN,
    };
};

export const getCallItemContactSubtitle = (
    title,
    contact,
    phoneNumber,
    ownCountryCode,
    i18next,
) => {
    const isTechnicalNumber = isTechnicalNumberContact(contact, phoneNumber);

    if (title.type === ContactRowType.CONTACT_NAME) {
        const companyName = getContactCompanyName(contact);
        if (companyName) {
            return {
                text: companyName,
                type: ContactRowType.COMPANY_NAME,
            };
        }
        if (isTechnicalNumber) {
            return {
                text: '',
                type: ContactRowType.TECHNICALNUMBER,
            };
        } else {
            return {
                text: prettyFormatE164Number(phoneNumber, ownCountryCode),
                type: ContactRowType.NUMBER,
            };
        }
    }
    if (title.type === ContactRowType.COMPANY_NAME) {
        if (isTechnicalNumber) {
            return {
                text: '',
                type: ContactRowType.TECHNICALNUMBER,
            };
        } else {
            return {
                text: prettyFormatE164Number(phoneNumber, ownCountryCode),
                type: ContactRowType.NUMBER,
            };
        }
    }
    if (title.type === ContactRowType.NUMBER) {
        if (isTechnicalNumber) {
            return {
                text: '',
                type: ContactRowType.TECHNICALNUMBER,
            };
        } else {
            const callerCountryCode = getCountryCodeFromNumber(phoneNumber);
            if (callerCountryCode) {
                return {
                    text: countries.getName(callerCountryCode, moment.locale()),
                    type: ContactRowType.COUNTRY_NAME,
                };
            }
            return {
                text: i18next.t('Contact.Unknown'),
                type: ContactRowType.UNKNOWN,
            };
        }
    }
    if (title.type === ContactRowType.UNKNOWN) {
        return {
            text: i18next.t('call.hidden_number'),
            type: ContactRowType.HIDDEN,
        };
    }
    return undefined;
};

export const getCallItemContactExtra = (
    title,
    subtitle,
    contact,
    phoneNumber,
    ownCountryCode,
) => {
    if (
        title.type === ContactRowType.CONTACT_NAME &&
        subtitle.type === ContactRowType.NUMBER
    ) {
        const department = getContactDepartment(contact);
        if (department) {
            return {
                text: department,
                type: ContactRowType.DEPARTMENT_NAME,
            };
        }
    }
    if (
        title.type === ContactRowType.CONTACT_NAME &&
        subtitle.type === ContactRowType.COMPANY_NAME
    ) {
        const isTechnicalNumber = isTechnicalNumberContact(
            contact,
            phoneNumber,
        );

        if (isTechnicalNumber) {
            return {
                text: '',
                type: ContactRowType.TECHNICALNUMBER,
            };
        }

        return {
            text: prettyFormatE164Number(phoneNumber, ownCountryCode),
            type: ContactRowType.NUMBER,
        };
    }
    return undefined;
};

export const getCallItemContactSubtitleExtended = (
    title,
    contact,
    phoneNumber,
    ownCountryCode,
    i18next,
) => {
    const subtitle = getCallItemContactSubtitle(
        title,
        contact,
        phoneNumber,
        ownCountryCode,
        i18next,
    );
    const extraInfo = getCallItemContactExtra(
        title,
        subtitle,
        contact,
        phoneNumber,
        ownCountryCode,
    );

    if (
        subtitle.type === ContactRowType.COMPANY_NAME &&
        extraInfo &&
        extraInfo.type === ContactRowType.NUMBER
    ) {
        return `${extraInfo.text}\u00A0\u00A0\u00A0${subtitle.text}`;
    }

    if (
        subtitle.type === ContactRowType.NUMBER &&
        extraInfo &&
        extraInfo.type === ContactRowType.DEPARTMENT_NAME
    ) {
        return `${subtitle.text}\u00A0\u00A0\u00A0${extraInfo.text}`;
    }
    return subtitle.text;
};

// Returns an object of the subtitle to be able to distinguish between parts that need to use <NumberWrapper> and those that don't for proper right-to-left support.
export const getCallItemContactSubtitleExtendedObject = (
    title,
    contact,
    phoneNumber,
    ownCountryCode,
    i18next,
) => {
    const subtitle = getCallItemContactSubtitle(
        title,
        contact,
        phoneNumber,
        ownCountryCode,
        i18next,
    );
    const extraInfo = getCallItemContactExtra(
        title,
        subtitle,
        contact,
        phoneNumber,
        ownCountryCode,
    );

    let firstPart = { text: subtitle.text, type: subtitle.type };
    let space = '';
    let secondPart = { text: '', type: '' };

    if (
        subtitle.type === ContactRowType.COMPANY_NAME &&
        extraInfo &&
        extraInfo.type === ContactRowType.NUMBER
    ) {
        firstPart = { text: extraInfo.text, type: extraInfo.type };
        space = '\u00A0\u00A0\u00A0';
        secondPart = { text: subtitle.text, type: subtitle.type };
    }

    if (
        subtitle.type === ContactRowType.NUMBER &&
        extraInfo &&
        extraInfo.type === ContactRowType.DEPARTMENT_NAME
    ) {
        space = '\u00A0\u00A0\u00A0';
        secondPart = { text: extraInfo.text, type: extraInfo.type };
    }

    return {
        firstPart,
        space,
        secondPart,
    };
};
